/** "Your cars": every car of theirs, where it is, and each car's journey end to end. */
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, FileText, Upload } from "lucide-react";
import { openMyDocument, useMyAccount, useMyCar, useMyCars, useMyPromises, useUploadPaper } from "@/services/python-api/customerHooks";
import { Card, Empty, LeftOff, Loading, Page, Pill, STAGE_TONE, day } from "./parts";

export function MyCars() {
  const { data, isLoading } = useMyCars();
  const navigate = useNavigate();
  const cars = data?.cars ?? [];
  return (
    <Page title="Your cars">
      {isLoading ? (
        <Loading />
      ) : cars.length === 0 ? (
        <Empty title="No cars yet">Cars we ship for you appear here.</Empty>
      ) : (
        <div className="space-y-3">
          {cars.map((car) => (
            <Card
              key={car.last6}
              role="button"
              className="flex cursor-pointer items-center gap-3 hover:border-[#D0D5DD]"
              onClick={() => navigate(`/my-cars/${car.last6}`)}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-[15px] font-semibold text-[#101928]">{car.car}</p>
                <p className="text-[13px] text-[#667185]">…{car.last6}</p>
                <div className="mt-2">
                  {car.confirming ? (
                    <Pill>We're confirming the latest</Pill>
                  ) : (
                    <Pill tone={STAGE_TONE[car.stage]}>{car.stage_label}</Pill>
                  )}
                  {car.shipping?.eta ? (
                    <span className="ml-2 text-[13px] text-[#475367]">ETA {day(car.shipping.eta)}</span>
                  ) : null}
                </div>
                <LeftOff line={car.left_off} compact />
              </div>
              <ChevronRight size={18} color="#98A2B3" />
            </Card>
          ))}
          {data?.still_confirming ? (
            <p className="px-1 text-[13px] text-[#667185]">
              {data.still_confirming} more {data.still_confirming === 1 ? "car" : "cars"} we're still confirming with
              the team.
            </p>
          ) : null}
        </div>
      )}
    </Page>
  );
}

function Fact({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-4 py-2 text-[14px]">
      <span className="text-[#667185]">{label}</span>
      <span className="text-right font-medium text-[#1D2739]">{value}</span>
    </div>
  );
}

const PAPER_KINDS = [
  ["dock_receipt", "Dock receipt"],
  ["bol", "Bill of lading"],
  ["telex_release", "Telex release"],
  ["gate_release", "Gate release"],
  ["title", "Title"],
  ["purchase_receipt", "Purchase receipt"],
  ["bill_of_sale", "Bill of sale"],
  ["other", "Other"],
];

/** Anyone on the team, or their forwarder, adds a paper; Dockie checks it before filing. */
function AddPaper({ last6, uploads }) {
  const upload = useUploadPaper();
  const [kind, setKind] = React.useState("dock_receipt");
  const error = upload.error?.response?.data?.detail;
  return (
    <Card className="mt-3">
      <p className="text-[14px] font-semibold text-[#101928]">Add a paper</p>
      <p className="text-[13px] text-[#667185]">Dock receipts, bills of lading and the like. Dockie checks each one.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        {PAPER_KINDS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setKind(key)}
            className={`rounded-full border px-3 py-1 text-[13px] ${
              kind === key ? "border-[#2563EB] bg-[#EFF6FF] text-[#1D4ED8]" : "border-[#E4E7EC] text-[#344054]"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <label className="mt-3 flex h-11 cursor-pointer items-center justify-center gap-2 rounded-full border border-[#D0D5DD] text-[15px] font-medium text-[#344054] hover:bg-[#F9FAFB]">
        <Upload size={16} /> {upload.isPending ? "Uploading…" : "Choose a file"}
        <input
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.heic"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && upload.mutate({ last6, kind, file: e.target.files[0] })}
        />
      </label>
      {error ? <p className="mt-2 text-[13px] text-[#CB1A14]">{error}</p> : null}
      {uploads?.length ? (
        <ul className="mt-3 divide-y divide-[#F0F2F5]">
          {uploads.map((u) => (
            <li key={u.id} className="flex items-center justify-between py-2 text-[13px]">
              <span className="text-[#101928]">
                {u.kind} <span className="text-[#667185]">· {u.from}</span>
              </span>
              <span className={u.checked ? "text-[#036B26]" : "text-[#865503]"}>
                {u.checked ? "Checked by Dockie" : "Dockie is checking"}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}

/** The promise this car is on, in dates we can prove (Val's guide, section 8). */
function OurPromise({ last6 }) {
  const { data } = useMyPromises();
  const p = (data?.cars ?? []).find((c) => c.last6 === last6);
  if (!p) return null;
  const when = (v) => (v && /^\d{4}-/.test(v) ? day(v) : v);
  const rows = [
    ["Car at the yard", when(p.car_in)],
    ["Title with us", when(p.title_in) || "Not yet"],
    ["Clock starts", when(p.clock_starts)],
    ["Loads by", when(p.load_by)],
    ["At the port by", when(p.port_by)],
  ];
  return (
    <Card className="mt-3">
      <div className="flex items-center justify-between">
        <p className="text-[14px] font-semibold text-[#101928]">Our promise</p>
        <span className="text-[12px] text-[#667185]">
          {p.method === "fcl" ? "Full container" : "Shared container"} · {p.days_at_yard} days at the yard
        </span>
      </div>
      {p.estimate ? <p className="mt-1 text-[12px] text-[#865503]">Dates are an {p.estimate}</p> : null}
      <div className="mt-2 divide-y divide-[#F0F2F5]">
        {rows.map(([label, value]) => (
          <div key={label} className="flex justify-between gap-4 py-2 text-[14px]">
            <span className="text-[#667185]">{label}</span>
            <span className={`text-right font-medium ${p.late && label === "Loads by" ? "text-[#CB1A14]" : "text-[#1D2739]"}`}>
              {value}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

export function MyCar() {
  const { last6 } = useParams();
  const navigate = useNavigate();
  const { data: car, isLoading, isError } = useMyCar(last6);
  const { data: account } = useMyAccount();
  const back = (
    <button className="flex items-center text-[14px] text-[#2563EB]" onClick={() => navigate("/my-cars")}>
      <ChevronLeft size={16} /> Your cars
    </button>
  );
  if (isLoading) return <Page title="Car">{back}<div className="mt-3"><Loading /></div></Page>;
  if (isError || !car) return <Page title="Car">{back}<div className="mt-3"><Empty title="We can't find that car" /></div></Page>;
  return (
    <Page title={car.car}>
      {back}
      <h2 className="mt-3 text-[20px] font-semibold text-[#101928] lg:hidden">{car.car}</h2>
      <p className="mt-0.5 text-[13px] text-[#667185]">…{car.last6}</p>
      {car.left_off ? (
        <div className="mt-4">
          <LeftOff line={car.left_off} />
        </div>
      ) : null}
      {car.confirming ? (
        <Card className="mt-4">
          <p className="text-[14px] text-[#475367]">We're confirming the latest on this car and will update it here.</p>
        </Card>
      ) : (
        <Card className="mt-4">
          <ol className="relative">
            {car.timeline.map((step, i) => (
              <li key={step.key} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <span
                    className={`mt-0.5 h-3.5 w-3.5 rounded-full border-2 ${
                      step.state === "done"
                        ? "border-[#2563EB] bg-[#2563EB]"
                        : step.state === "current"
                        ? "border-[#2563EB] bg-white ring-4 ring-[#DBEAFE]"
                        : "border-[#D0D5DD] bg-white"
                    }`}
                  />
                  {i < car.timeline.length - 1 ? (
                    <span className={`w-0.5 flex-1 ${step.state === "done" ? "bg-[#2563EB]" : "bg-[#E4E7EC]"}`} />
                  ) : null}
                </div>
                <div className={`-mt-0.5 ${i < car.timeline.length - 1 ? "pb-5" : ""}`}>
                  <p className={`text-[14px] ${step.state === "next" ? "text-[#98A2B3]" : "font-medium text-[#101928]"}`}>
                    {step.label}
                  </p>
                  {step.on ? <p className="text-[13px] text-[#667185]">{day(step.on)}</p> : null}
                </div>
              </li>
            ))}
          </ol>
        </Card>
      )}
      {/* Only what the record has: a card of blanks says nothing. */}
      {[car.shipping?.line, car.shipping?.vessel, car.shipping?.eta, car.title?.status].some(Boolean) ? (
        <Card className="mt-3 divide-y divide-[#F0F2F5]">
          <Fact label="Shipping line" value={car.shipping.line} />
          <Fact label="Vessel" value={car.shipping.vessel} />
          <Fact label="From" value={car.shipping.from} />
          <Fact label="To" value={car.shipping.to} />
          <Fact label="Arriving" value={day(car.shipping.eta)} />
          <Fact label="Telex released" value={day(car.shipping.telex_released)} />
          <Fact label="Title" value={car.title?.status} />
          <Fact label="Title tracking" value={car.title?.tracking && `${car.title.courier ?? ""} ${car.title.tracking}`} />
        </Card>
      ) : null}
      {car.documents?.length ? (
        <Card className="mt-3">
          <p className="mb-1 text-[14px] font-semibold text-[#101928]">Papers</p>
          {car.documents.map((d) => (
            <button
              key={d.id}
              className="flex w-full items-center gap-2 py-2 text-left text-[14px] text-[#2563EB]"
              onClick={async () => window.open(await openMyDocument(d.id), "_blank", "noopener")}
            >
              <FileText size={16} /> {d.kind}
            </button>
          ))}
        </Card>
      ) : null}
      {account?.join && !account?.partner ? <OurPromise last6={car.last6} /> : null}
      {account?.partners ? <AddPaper last6={car.last6} uploads={car.uploads} /> : null}
      {car.invoices?.map((inv) => (
        <Card key={inv.id} className="mt-3 flex items-center justify-between">
          <div>
            <p className="text-[14px] font-semibold text-[#101928]">Invoice {inv.number}</p>
            <p className="text-[13px] text-[#667185]">Balance {inv.balance}</p>
          </div>
          <button className="text-[14px] text-[#2563EB]" onClick={() => navigate("/my-invoices")}>
            View
          </button>
        </Card>
      ))}
    </Page>
  );
}
